// src/pages/image-management.tsx

import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
// REQUIRED: Import all Select components from your Shadcn/UI path
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"; 

// Use the exact name of the newly created public bucket
const ABOUT_BUCKET_NAME = 'fca-site-images';

// Define all manageable images with their unique IDs
const ManageableImages = [
  { id: 'about', label: 'About Section Image' },
  { id: 'pricing', label: 'Pricing Section Cake Image' },
  { id: 'hero-1', label: 'Hero Carousel Slide 1' },
  { id: 'hero-2', label: 'Hero Carousel Slide 2' },
  { id: 'hero-3', label: 'Hero Carousel Slide 3' },
  { id: 'hero-4', label: 'Hero Carousel Slide 4' },
];

export default function ImageManagementPage() {
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>('');
  // NEW STATE: Track the currently selected image ID
  const [selectedImageId, setSelectedImageId] = useState<string>('about'); 

  // NEW: Refactored useEffect to accept and use the selected ID
  const fetchImageUrl = async (id: string) => {
    try {
      // Calls the new generalized API with the specific image ID
      const r = await fetch(`/api/site-image?id=${id}`, { method: 'GET' }); 
      const j = await r.json();
      if (j?.ok) setCurrentUrl(j.url || '');
      else setCurrentUrl('');
    } catch { setCurrentUrl(''); }
  };
  
  // Fetch current image URL whenever the selected ID changes
  useEffect(() => {
    document.title = 'Image Management • ' + selectedImageId.toUpperCase();
    fetchImageUrl(selectedImageId);
  }, [selectedImageId]); 

  // Function to handle file selection
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMsg('');
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const uploadAndSave = async () => {
    if (!file) { setMsg('Please choose an image'); return; }
    setBusy(true); setMsg('');

    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      // Generate a key using the selected ID
      const key = `${selectedImageId}/${Date.now()}.${ext}`; 

      // 1. UPLOAD: Use the correct bucket name (fca-site-images)
      const { error: upErr } = await supabase.storage.from(ABOUT_BUCKET_NAME).upload(key, file, {
        upsert: true,
        cacheControl: '3600',
        contentType: file.type || 'image/png',
      });
      if (upErr) throw upErr;

      // 2. GET PUBLIC URL: Use the correct bucket name (fca-site-images)
      const { data } = supabase.storage.from(ABOUT_BUCKET_NAME).getPublicUrl(key);
      const publicUrl = data?.publicUrl;
      if (!publicUrl) throw new Error('No public URL returned');

      // 3. Save URL via generalized API, passing the ID
      const r = await fetch('/api/site-image', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedImageId, url: publicUrl }), 
      });
      const j = await r.json();
      if (!j?.ok) throw new Error(j?.error || 'Failed to save image URL');

      setCurrentUrl(publicUrl);
      setMsg(`Updated ${selectedImageId} image successfully!`);
    } catch (err: any) {
      setMsg(`Error: ${err?.message || 'upload failed'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-4">Image Management</h1>
      
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Select Image to Manage</label>
        <Select value={selectedImageId} onValueChange={setSelectedImageId}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select an image slot" />
          </SelectTrigger>
          <SelectContent>
            {ManageableImages.map(img => (
              <SelectItem key={img.id} value={img.id}>
                {img.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <section className="mb-6">
        <h2 className="text-lg font-medium mb-2">Current Image for "{ManageableImages.find(i=>i.id===selectedImageId)?.label}"</h2>
        {currentUrl ? (
          <img src={currentUrl} alt={`Current ${selectedImageId}`} className="rounded-xl border w-full max-w-xl" />
        ) : (
          <p className="text-muted-foreground">No custom image set. Using local asset fallback.</p>
        )}
      </section>

      <section className="space-y-3">
        <input type="file" accept="image/*" onChange={onFile} />
        <div>
          <button
            onClick={uploadAndSave}
            disabled={busy || !file}
            className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
          >
            {busy ? 'Uploading…' : `Upload & Set as ${selectedImageId} Image`}
          </button>
        </div>
        {msg && <p className="text-sm">{msg}</p>}
      </section>
    </main>
  );
}